import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Login.module.css";
import { CustomTextInput } from "../../components";
import { useAuth } from "../../hooks/useAuth";
import shape1 from "../../assets/images/forgot-password.png";
import logo1 from "../../assets/images/logo_1.png";
import AxiosInstance from "../../services/Api";

const API_BASE_URL = "http://localhost:8087/v1/api";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    if (apiError) {
      setApiError("");
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    const fieldError = validateField(name, formData[name]);
    if (fieldError) {
      setErrors((prev) => ({ ...prev, [name]: fieldError }));
    }
  };

  const validateField = (name, value) => {
    switch (name) {
      case "email":
        if (!value) return "Email / Username is required";
        return "";
      default:
        return "";
    }
  };

  const validate = () => {
    const newErrors = {};
    const emailError = validateField("email", formData.email);

    if (emailError) newErrors.email = emailError;

    return newErrors;
  };

  const isFormValid = formData.email && !errors.email;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    setTouched({ email: true });

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await AxiosInstance.post(`/login/forgotPassword`, {
        email: formData.email,
      });

      if (response.data.status) {
        setSuccessMessage(
          response.data.message ||
            "An email with instructions to reset your password has been sent to your registered email address.",
        );
        setShowSuccessModal(true);
      } else {
        setApiError(
          response.data.message || "Password reset failed. Please try again.",
        );
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      setApiError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalOk = () => {
    setShowSuccessModal(false);
    navigate("/");
  };

  const handleHomeClick = () => {
    navigate("/");
  };

  const getFieldError = (fieldName) => {
    return touched[fieldName] ? errors[fieldName] : "";
  };

  if (isLoading || authLoading) {
    return (
      <div className={styles.loaderWrapper}>
        <div className={styles.loader}></div>
      </div>
    );
  }

  return (
    <div className={styles.mainPageWrapper}>
      <div className={styles.logSection}>
        <div className={styles.container}>
          <div className={styles.loginBox}>
            {/* Left Column */}
            <div className={styles.leftColumn}>
              <div className={styles.brandingContent}>
                <img
                  src={shape1}
                  alt="Education Illustration"
                  className={styles.illustrationImage}
                />
                <div className={styles.logoContainer}>
                  <img src={logo1} alt="iFillip Logo" className={styles.logo} />
                </div>
                <p className={styles.tagline}>
                  An omni-channel school management and learning augmentation
                  platform
                </p>
                <a
                  href="https://bharatclouds.com/"
                  className={styles.companyName}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Product of Bharat Clouds Pvt Ltd
                </a>
              </div>
            </div>

            {/* Right Column */}
            <div className={styles.rightColumn}>
              <h2 className={styles.loginTitle}>Forgot Password</h2>
              <p className={styles.disclaimer}>
                <strong>For Admin and Staff:</strong> Enter the email address
                associated with your account, and we will email you a link to
                reset your password.
              </p>
              <p className={styles.disclaimer}>
                <strong>For Students and Parents:</strong> Enter your username,
                and your password will be reset automatically.
              </p>

              {apiError && (
                <div className={styles.apiError}>
                  <span className={styles.errorIcon}>⚠</span>
                  {apiError}
                </div>
              )}

              <form
                name="forgot_password"
                id="forgot_password"
                onSubmit={handleSubmit}
                className={styles.loginForm}
                noValidate
              >
                <CustomTextInput
                  type="text"
                  name="email"
                  placeholder="Enter Your Email ID / Username"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={getFieldError("email")}
                  maxLength={100}
                  autoComplete="email"
                  required
                  disabled={isSubmitting}
                />

                <div className={styles.buttonGroup}>
                  <button
                    type="submit"
                    className={styles.homeButton}
                    disabled={!isFormValid || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className={styles.buttonLoader}></span>
                        Loading...
                      </>
                    ) : (
                      "Reset"
                    )}
                  </button>
                  {/* <button
                    type="button"
                    className={styles.loginButton}
                    onClick={handleHomeClick}
                    disabled={isSubmitting}
                  >
                    Home
                  </button> */}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Bootstrap Success Modal */}
      {showSuccessModal && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-labelledby="successModalLabel"
            aria-modal="true"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content" style={{ borderRadius: "8px" }}>
                <div
                  className="modal-body text-center"
                  style={{ padding: "40px 30px" }}
                >
                  {/* Success Checkmark Icon */}
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      border: "3px solid #c8e6c9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 25px",
                    }}
                  >
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#4caf50"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>

                  {/* Title */}
                  <h4
                    style={{
                      fontFamily: "var(--font-family-primary)",
                      fontWeight: "var(--font-weight-bold)",
                      fontSize: "24px",
                      color: "var(--text-primary)",
                      marginBottom: "12px",
                    }}
                  >
                    Recover your account!
                  </h4>

                  {/* Description */}
                  <p
                    style={{
                      fontFamily: "var(--font-family-primary)",
                      fontWeight: "var(--font-weight-regular)",
                      fontSize: "var(--font-size-sm)",
                      color: "var(--text-secondary)",
                      lineHeight: "1.6",
                      marginBottom: "25px",
                      padding: "0 20px",
                    }}
                  >
                    {successMessage}
                  </p>

                  {/* OK Button */}
                  <button
                    type="button"
                    className="btn"
                    onClick={handleModalOk}
                    style={{
                      backgroundColor: "#5bc0de",
                      color: "var(--color-white)",
                      fontFamily: "var(--font-family-primary)",
                      fontWeight: "var(--font-weight-semibold)",
                      fontSize: "var(--font-size-sm)",
                      padding: "10px 40px",
                      border: "none",
                      borderRadius: "var(--border-radius-sm)",
                      cursor: "pointer",
                      transition: "var(--transition-fast)",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#46b8da")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "#5bc0de")
                    }
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ForgotPassword;
